const registry = new Map()

export function supportsIntersectionObserver() {
  return typeof window !== 'undefined' && 'IntersectionObserver' in window
}

function normalizeThreshold(t) {
  if (t === undefined) {
    return undefined
  }
  const arr = Array.isArray(t) ? t.slice() : [t]
  return Array.from(new Set(arr.filter((x) => typeof x === 'number'))).sort(
    (a, b) => a - b
  )
}

function keyFor(options) {
  const rootId = options.root instanceof Element ? options.root.tagName : 'null'
  const margin = options.rootMargin || ''
  const threshold = normalizeThreshold(options.threshold) || []
  return `${rootId}|${margin}|${threshold.join(',')}`
}

function createNoopManager(normalized) {
  const handlers = new Map()
  const manager = {
    disconnect() {
      handlers.clear()
    },
    observe(el, handler) {
      const set = handlers.get(el) || new Set()
      set.add(handler)
      handlers.set(el, set)
      queueMicrotask(() => {
        try {
          handler(
            { intersectionRatio: 1, isIntersecting: true, target: el },
            null
          )
        } catch {
          /* empty */
        }
      })
      return () => manager.unobserve(el, handler)
    },
    options: normalized,
    get size() {
      return handlers.size
    },
    unobserve(el, handler) {
      const set = handlers.get(el)
      if (!set) return
      if (handler) set.delete(handler)
      if (!handler || set.size === 0) handlers.delete(el)
    }
  }
  return manager
}

function createObserverManager(normalized) {
  const targets = new Map()
  const observer = new IntersectionObserver((entries) => {
    for (const entry of entries) {
      const handlers = targets.get(entry.target)
      if (!handlers) continue
      handlers.forEach((handler) => {
        try {
          handler(entry, observer)
        } catch {
          /* empty */
        }
      })
    }
  }, normalized)

  const manager = {
    disconnect() {
      observer.disconnect()
      targets.clear()
    },
    observe(el, handler) {
      let set = targets.get(el)
      if (!set) {
        set = new Set()
        targets.set(el, set)
        observer.observe(el)
      }
      set.add(handler)
      return () => manager.unobserve(el, handler)
    },
    options: normalized,
    get size() {
      return targets.size
    },
    unobserve(el, handler) {
      const set = targets.get(el)
      if (!set) return
      if (handler) set.delete(handler)
      if (!handler || set.size === 0) {
        targets.delete(el)
        observer.unobserve(el)
      }
    }
  }
  return manager
}

export function useIntersection(options = {}) {
  const normalized = {
    ...options,
    threshold: normalizeThreshold(options.threshold)
  }
  const key = keyFor(normalized)

  const existing = registry.get(key)
  if (existing) {
    return existing
  }

  const manager = supportsIntersectionObserver()
    ? createObserverManager(normalized)
    : createNoopManager(normalized)

  registry.set(key, manager)
  return manager
}
